/**
 * Active semester: single source of truth for the current BHG semester.
 * 12-week semester; valid session dates are the 12 Thursdays from startDate,
 * plus the optional semester make-up day when set (attendance + visit picker).
 */

export interface ActiveSemester {
  /** First Thursday of the semester (YYYY-MM-DD) */
  startDate: string;
  /** Last Thursday of the semester (12 weeks later) */
  endDate: string;
  /** Display label e.g. "Spring 2026" */
  label: string;
  /**
   * Selectable session dates (YYYY-MM-DD): the 12 regular Thursdays, plus the
   * semester make-up day when set (sorted ascending, no duplicates).
   */
  thursdays: string[];
  /** Formatted for hero/copy: "Thursday, January 8th" */
  startDateFormatted: string;
}

const SEMESTER_WEEKS = 12;

/**
 * Format YYYY-MM-DD for form value.
 */
export function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse YYYY-MM-DD to Date (local).
 */
export function fromDateString(s: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const date = new Date(`${s  }T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Returns Thursdays that are strictly after today (YYYY-MM-DD).
 * Today is not selectable; use for visit picker so the next available Thursday is first.
 */
export function getSelectableThursdaysFromToday(thursdays: string[]): string[] {
  const today = toDateString(new Date());
  return thursdays.filter((d) => d > today);
}

/**
 * Build exactly 12 Thursdays from start date string (YYYY-MM-DD).
 * Start date + 11 more (start+7, +14, ... +77 days). Uses only string/number math, no Date getters (avoids timezone).
 */
function buildThursdays(startDateStr: string): string[] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDateStr);
  if (!match) return [];
  const y = Number(match[1]);
  const m = Number(match[2]) - 1; // 0-based month for Date
  const startDay = Number(match[3]);
  const out: string[] = [];
  for (let i = 0; i < SEMESTER_WEEKS; i++) {
    const d = new Date(y, m, startDay + i * 7);
    out.push(toDateString(d));
  }
  return out;
}

/**
 * Derive label from start date: "Spring YYYY" or "Fall YYYY".
 */
function labelFromStartDate(startDateStr: string): string {
  const d = fromDateString(startDateStr);
  if (!d) return '';
  const month = d.getMonth(); // 0 = Jan, 8 = Sep
  const year = d.getFullYear();
  if (month <= 1) return `Spring ${year}`;
  if (month >= 8) return `Fall ${year}`;
  return `${year}`;
}

/** First Thursday of the current active semester. Update when admin is dynamic. */
const ACTIVE_SEMESTER_START = '2026-01-08';

const thursdays = buildThursdays(ACTIVE_SEMESTER_START);
const startDate = ACTIVE_SEMESTER_START;
const endDate = thursdays.length > 0 ? thursdays[thursdays.length - 1] : startDate;
const label = labelFromStartDate(startDate);

/**
 * Active semester config. Use everywhere and format for context.
 */
export const activeSemester: ActiveSemester = {
  startDate,
  endDate,
  label,
  thursdays,
  startDateFormatted: '', // set below after helper is defined
};

// Set formatted string (avoids circular dependency on helper)
activeSemester.startDateFormatted = formatSemesterStartForDisplay(startDate);

/**
 * Format the semester start for hero/copy: "Thursday, January 8th".
 */
function dayOfMonthSuffix(day: number): string {
  if (day === 1 || day === 21 || day === 31) return 'st';
  if (day === 2 || day === 22) return 'nd';
  if (day === 3 || day === 23) return 'rd';
  return 'th';
}

export function formatSemesterStartForDisplay(startDateStr: string): string {
  const d = fromDateString(startDateStr);
  if (!d) return '';
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const day = d.getDate();
  const suffix = dayOfMonthSuffix(day);
  return `${dayName}, ${month} ${day}${suffix}`;
}

/**
 * Epoch ms to local date string (YYYY-MM-DD).
 */
export function epochToDateString(ts: number): string {
  return toDateString(new Date(ts));
}

/**
 * First Thursday on or after the given date (epoch ms). Used to derive 12 Thursdays from API semester.startDate.
 */
export function getFirstThursdayFromEpoch(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = d.getMonth();
  const date = d.getDate();
  const local = new Date(y, m, date);
  const dayOfWeek = local.getDay(); // 0 Sun .. 4 Thu .. 6 Sat
  const daysUntilThursday = (4 - dayOfWeek + 7) % 7;
  const firstThursday = new Date(y, m, date + daysUntilThursday);
  return toDateString(firstThursday);
}

/**
 * Append a semester make-up day (epoch ms) to the regular Thursday list when set.
 * Dedupes and sorts ascending. Returns the original list when makeupDate is missing/invalid.
 */
export function withMakeupSessionDate(
  thursdays: string[],
  makeupDate?: number | null
): string[] {
  if (makeupDate == null || !Number.isFinite(makeupDate)) {
    return thursdays;
  }
  const makeupStr = epochToDateString(makeupDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(makeupStr)) {
    return thursdays;
  }
  if (thursdays.includes(makeupStr)) {
    return thursdays;
  }
  return [...thursdays, makeupStr].sort((a, b) => a.localeCompare(b));
}

/**
 * Build ActiveSemester from public API payload (startDate/endDate as epoch ms).
 * Optional makeupDate (epoch ms) is included in `thursdays` for attendance and visits.
 */
export function buildActiveSemesterFromApi(payload: {
  name: string;
  startDate: number;
  endDate: number;
  makeupDate?: number | null;
}): ActiveSemester {
  const firstThursdayStr = getFirstThursdayFromEpoch(payload.startDate);
  const regularThursdays = buildThursdays(firstThursdayStr);
  const thursdays = withMakeupSessionDate(regularThursdays, payload.makeupDate);
  const endDateStr =
    regularThursdays.length > 0
      ? regularThursdays[regularThursdays.length - 1]
      : firstThursdayStr;
  return {
    startDate: firstThursdayStr,
    endDate: endDateStr,
    label: labelFromStartDate(firstThursdayStr),
    thursdays,
    startDateFormatted: formatSemesterStartForDisplay(firstThursdayStr),
  };
}
